import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, Sparkles } from "lucide-react";
import { getProductBySlug, PRODUCTS } from "@/data/products";
import { ProductHeader, ProductFooter } from "@/pages/Products";

export default function ProductDetail() {
  const { slug } = useParams();
  const product = getProductBySlug(slug);

  useEffect(() => {
    if (product) document.title = `${product.name} — Cravy Smart Markets`;
    window.scrollTo(0, 0);
  }, [product]);

  if (!product) return <Navigate to="/products" replace />;

  const others = PRODUCTS.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <div className="font-body bg-cravy-cream min-h-screen">
      <ProductHeader />

      {/* Hero */}
      <section className="bg-cravy-ink cravy-grain pt-[72px] relative overflow-hidden">
        <div className="absolute -top-32 -right-20 w-[520px] h-[520px] rounded-full bg-cravy-orange/15 blur-[120px]" />
        <div className="absolute -bottom-40 -left-32 w-[460px] h-[460px] rounded-full bg-cravy-orangeDeep/10 blur-[120px]" />

        <div className="relative max-w-7xl mx-auto px-5 lg:px-10 pt-14 lg:pt-20 pb-16 lg:pb-24">
          <Link to="/products" className="text-cravy-cream/60 hover:text-cravy-orange text-[13px] inline-flex items-center gap-2" data-testid="product-detail-back">
            <ArrowLeft className="w-4 h-4" /> All products
          </Link>

          <div className="mt-8 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7 order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cravy-orange/10 border border-cravy-orange/25 text-cravy-orange text-[11px] font-semibold tracking-wide uppercase">
                {product.badge ? <><Sparkles className="w-3.5 h-3.5" /> {product.badge}</> : product.category}
              </div>
              <h1 className="mt-5 font-display font-extrabold text-cravy-cream tracking-[-0.03em] leading-[0.95] text-[40px] sm:text-[56px] lg:text-[68px]">
                {product.name}
              </h1>
              <p className="mt-5 text-cravy-orange italic font-display text-[20px] lg:text-[24px] leading-snug">
                {product.tagline}
              </p>
              <p className="mt-6 text-cravy-cream/75 text-[16px] lg:text-[17px] max-w-xl leading-relaxed">
                {product.short}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to="/#partner" className="btn-orange inline-flex items-center gap-2 px-7 py-4 rounded-full text-[15px]" data-testid="product-cta-request">
                  Request this market <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/products" className="btn-ghost-dark inline-flex items-center gap-2 px-7 py-4 rounded-full text-[15px] font-semibold" data-testid="product-cta-other">
                  See other formats
                </Link>
              </div>
            </div>

            <div className="lg:col-span-5 order-1 lg:order-2">
              <div className="relative rounded-[28px] bg-gradient-to-br from-cravy-cream to-white border border-white/10 p-6 shadow-2xl">
                <img src={product.image} alt={product.name} className="w-full h-auto object-contain max-h-[460px]" />
                <div className="absolute -bottom-4 -right-4 bg-cravy-orange text-cravy-ink rounded-full px-4 py-2 shadow-lg rotate-[5deg]">
                  <div className="font-brush text-[18px] leading-none">{product.category}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Description + Features + Specs */}
      <section className="bg-cravy-cream py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="text-cravy-orangeDeep font-semibold text-[12px] uppercase tracking-[0.25em]">About this format</div>
            <h2 className="mt-3 font-display font-extrabold text-[32px] sm:text-[42px] leading-[1.05] tracking-[-0.02em]">
              {product.tagline}
            </h2>
            <div className="mt-7 space-y-5 text-cravy-ink/75 text-[16px] leading-[1.75]">
              {product.description.map((para, i) => <p key={i}>{para}</p>)}
            </div>

            <div className="mt-12">
              <div className="text-cravy-orangeDeep font-semibold text-[12px] uppercase tracking-[0.25em] mb-4">Key features</div>
              <ul className="space-y-3">
                {product.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-3 text-cravy-ink/85 text-[15px]">
                    <span className="shrink-0 mt-0.5 w-5 h-5 rounded-full bg-cravy-orange/20 text-cravy-orangeDeep flex items-center justify-center">
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="rounded-3xl bg-cravy-ink text-cravy-cream p-7 lg:p-8 sticky top-[92px]">
              <div className="text-cravy-orange font-semibold text-[12px] uppercase tracking-[0.25em] mb-5">Specs</div>
              <dl className="space-y-4 mb-8">
                {product.specs.map((s, i) => (
                  <div key={i} className="flex items-baseline justify-between gap-4 border-b border-white/10 pb-3 last:border-b-0 last:pb-0">
                    <dt className="text-cravy-cream/55 text-[12px] uppercase tracking-[0.15em]">{s.k}</dt>
                    <dd className="font-display font-bold text-right text-[15px]">{s.v}</dd>
                  </div>
                ))}
              </dl>

              <div className="text-cravy-orange font-semibold text-[12px] uppercase tracking-[0.25em] mb-4">Best for</div>
              <ul className="space-y-2.5 mb-7">
                {product.bestFor.map((b, i) => (
                  <li key={i} className="text-cravy-cream/85 text-[14px] flex items-start gap-2">
                    <span className="text-cravy-orange mt-0.5">→</span> {b}
                  </li>
                ))}
              </ul>

              <Link to="/#partner" className="btn-orange w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-[14px]" data-testid="product-sidebar-cta">
                Request this format <ArrowRight className="w-4 h-4" />
              </Link>
              <div className="text-cravy-cream/45 text-[11px] text-center mt-3">$0 to your property · 30-day cancellation</div>
            </div>
          </aside>
        </div>
      </section>

      {/* You might also like */}
      <section className="bg-cravy-cream2 py-20">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="flex items-end justify-between gap-6 mb-10">
            <div>
              <div className="text-cravy-orangeDeep font-semibold text-[12px] uppercase tracking-[0.25em]">You might also like</div>
              <h3 className="mt-3 font-display font-extrabold text-[32px] sm:text-[40px] tracking-[-0.02em]">More formats to consider</h3>
            </div>
            <Link to="/products" className="hidden sm:inline-flex items-center gap-2 text-cravy-ink/70 hover:text-cravy-ink text-[14px] font-semibold">
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {others.map((p) => (
              <Link key={p.slug} to={`/products/${p.slug}`} className="group bg-white rounded-3xl p-5 border border-cravy-ink/5 hover:shadow-xl hover:-translate-y-1 transition-all" data-testid={`related-${p.slug}`}>
                <div className="aspect-square rounded-2xl bg-white flex items-center justify-center overflow-hidden">
                  <img src={p.image} alt={p.name} className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="mt-4">
                  <div className="text-cravy-orangeDeep text-[10px] font-bold uppercase tracking-[0.15em]">{p.category}</div>
                  <div className="mt-1.5 font-display font-bold text-[18px] tracking-tight">{p.name}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <ProductFooter />
    </div>
  );
}
